import { useI18n } from '@/hooks/useI18n';
import type { PublicContentListItem } from '../types';

const KIND_ICON: Record<string, string> = {
  NEWS: '📰',
  QUICK_POLL: '📊',
  ACHIEVEMENT: '🏆',
  EVENT: '📅',
  COMPETITION: '🎖️',
  MATERIAL: '📚',
  EDUCATION: '🎓',
  VACANCY: '💼',
};

const KIND_COLOR: Record<string, string> = {
  NEWS: '#41d2ff',
  QUICK_POLL: '#a78bfa',
  ACHIEVEMENT: '#fbbf24',
  EVENT: '#34d399',
  COMPETITION: '#f97316',
  MATERIAL: '#38bdf8',
  EDUCATION: '#4ade80',
  VACANCY: '#fb7185',
};

interface ContentCardProps {
  item: PublicContentListItem;
  onClick: (item: PublicContentListItem) => void;
  onSave?: (item: PublicContentListItem) => void;
  compact?: boolean;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function isExpiringSoon(iso?: string): boolean {
  if (!iso) return false;
  const diff = new Date(iso).getTime() - Date.now();
  return diff > 0 && diff < 7 * 24 * 3600 * 1000;
}

export const ContentCard = ({ item, onClick, onSave, compact }: ContentCardProps) => {
  const { t } = useI18n();
  const color = KIND_COLOR[item.kind] || '#41d2ff';
  const icon = KIND_ICON[item.kind] || '📄';
  const expiring = isExpiringSoon(item.deadline);
  const expired = item.deadline ? new Date(item.deadline) < new Date() : false;

  return (
    <div
      className="public-result"
      style={{ cursor: 'pointer', position: 'relative', border: `1px solid ${color}22` }}
      onClick={() => onClick(item)}
    >
      {item.is_important && (
        <span style={{ position: 'absolute', top: 10, right: 10, fontSize: 10, fontWeight: 900, color: '#fbbf24', background: '#fbbf2418', padding: '3px 7px', borderRadius: 20 }}>
          ВАЖНОЕ
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: 900, color, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {t(`publisher.kind.${item.kind}` as any) || item.kind}
        </span>
        {item.is_promoted && (
          <span style={{ fontSize: 9, fontWeight: 900, color: '#fbbf24', background: '#fbbf2415', padding: '2px 6px', borderRadius: 10, marginLeft: 'auto' }}>
            ★ ТОП
          </span>
        )}
      </div>
      <h4 style={{ margin: '0 0 6px', fontSize: 14.5, lineHeight: 1.4 }}>{item.title}</h4>
      {!compact && (
        <p style={{ margin: '0 0 10px', color: 'var(--text-dim)', fontSize: 12.5, lineHeight: 1.5 }}>
          {item.summary.length > 120 ? item.summary.slice(0, 120) + '…' : item.summary}
        </p>
      )}

      {item.deadline && (
        <div style={{ fontSize: 11, color: expired ? '#f87171' : expiring ? '#fbbf24' : 'var(--text-dim)', marginBottom: 6 }}>
          {expired ? '⚠ Истёк: ' : expiring ? '⏰ Дедлайн: ' : '📅 '}
          {formatDate(item.deadline)}
        </div>
      )}

      {item.event_start && !item.deadline && (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 6 }}>
          📅 {formatDate(item.event_start)}
        </div>
      )}

      {(item.meta?.city || item.meta?.level || item.meta?.format_type) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {item.meta?.city && <span className="public-pill">{item.meta.city}</span>}
          {item.meta?.level && <span className="public-pill">{item.meta.level}</span>}
          {item.meta?.format_type && <span className="public-pill">{item.meta.format_type}</span>}
          {item.meta?.cost_type === 'FREE' && <span style={{ fontSize: 10, fontWeight: 900, color: '#4ade80', background: '#4ade8015', padding: '3px 7px', borderRadius: 20 }}>Бесплатно</span>}
        </div>
      )}

      {item.hashtags.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          {item.hashtags.slice(0, 3).map(tag => (
            <span key={tag} style={{ fontSize: 10, color: 'var(--text-dim)' }}>#{tag}</span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
        <div style={{ display: 'flex', gap: 12, color: 'var(--text-dim)', fontSize: 11 }}>
          <span>👁 {item.view_count}</span>
          <span>🔖 {item.save_count}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onSave && (
            <button
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: 8,
                border: `1px solid ${item.user_has_saved ? '#41d2ff44' : 'var(--border-soft)'}`,
                background: item.user_has_saved ? '#41d2ff18' : 'transparent',
                color: item.user_has_saved ? '#41d2ff' : 'var(--text-dim)',
                cursor: 'pointer',
              }}
              onClick={e => { e.stopPropagation(); onSave(item); }}
            >
              {item.user_has_saved ? t('portal.detail.saved' as any) : t('portal.detail.save' as any)}
            </button>
          )}
          <button
            style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 8, border: `1px solid ${color}44`, background: `${color}12`, color, cursor: 'pointer' }}
            onClick={e => { e.stopPropagation(); onClick(item); }}
          >
            {t('common.open')}
          </button>
        </div>
      </div>

      {item.source_type === 'AUTO_OFFICIAL_SOURCE' && item.official_domain && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-soft)', fontSize: 10, color: 'var(--text-dim)' }}>
          🔗 {item.official_domain}
        </div>
      )}
    </div>
  );
};
